import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogTitle, Select, MenuItem, TextField, DialogActions, DialogContent, FormControlLabel, Checkbox } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Close from '@material-ui/icons/Close';
import { options, label } from '../../../../components/FlowConfigurationForms/TimeManagement/Schedule/config';
import triggerService from "../../../../services/triggerService/triggerService";
import { useSetRecoilState } from 'recoil';
import { alertState } from '../../../../recoil-store/recoil-state';
import Clock from "../../../../components/FlowConfigurationInputs/Clock/Clock";
import {DateTime as Datetime, DateTime} from 'luxon';

const useStyles = makeStyles((theme) => ({
  input: {
    backgroundColor: '#f2f2f2',
    color: '#d6d6d6'
  },
  bottomSection: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  formControl: {
    backgroundColor: '#F2F2F2 !important',
    width: '100%'
  },
  selectEmpty: {
    marginTop: theme.spacing(2)
  },
  select: {
    padding: '10px 24px 10px 10px',
    fontSize: 12,
    fontWeight: 600
  },
  button: {
    border: 'none',
    borderRadius: '5px',
    width: 100,
    height: 35,
    float: 'right',
    '&:focus': {
      outline: 'none'
    }
  },
  submit: {
    color: 'white',
    marginLeft: 15,
    backgroundColor: '#079063',
    cursor: 'pointer'
  }
}));

// Helper function to check if a given year is a leap year
const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

const Schedule = ({ setOpen, schedule, automId, automDetail }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [profile, setProfile] = useState({ trigger_profile_id: undefined, trigger_rule_id: undefined });
  const [scheduleType, setScheduleType] = useState('once');
  const [startDate, setStartDate] = useState('');
  const [specifiedDate, setSpecifiedDate] = useState();
  const [specifiedDay, setSpecifiedDay] = useState([]);
  const [specifiedMonth, setSpecifiedMonth] = useState([]);
  const [endDate, setEndDate] = useState('');
  const [specifiedTime, setSpecifiedTime] = useState('');
  const [minute, setMinute] = useState('1');
  const [isEndless, setIsEndless] = useState(false);
  const [dayOptions, setDayOptions] = useState([]);

  const setAlert = useSetRecoilState(alertState);
  const profileType = "SCHEDULE";


  useEffect(() => {
    if (specifiedMonth.length === 0) {
      setDayOptions([]);  // Clear day options if no month is selected
      setSpecifiedDay([]); // Clear selected days if no month is selected
      return;
    }

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    // Determine the correct year for each selected month
    const monthDays = specifiedMonth.map((month) => {
      const targetYear = month.value < currentMonth ? currentYear + 1 : currentYear;
      const daysInMonth = month.value === 2 ? (isLeapYear(targetYear) ? 29 : 28) : options.dropdownMonth.data.find((m) => m.value === month.value)?.days || 31;
      return daysInMonth;
    });

    // Get the minimum number of days among the selected months
    const minDays = Math.min(...monthDays, 31);

    // Update day options based on minDays
    setDayOptions(Array.from({ length: minDays }, (_, i) => ({ value: i + 1, label: `${i + 1}` })));

    // Filter selected days to remove any days that exceed minDays
    setSpecifiedDay((prevDays) => prevDays.filter((day) => day.value <= minDays));
  }, [specifiedMonth]);

  useEffect(() => {
    if (automId !== undefined && automId !== null) {
      async function fetchData() {
        const resp = await triggerService.getTriggerProfileByAutomId(automId, profileType);
        if (resp && resp.status && resp.code === 200) {
          const period = resp.data.trigger_rule.period;
          setScheduleType(period.scheduleType);
          if (period.scheduleType === 'once') {
            setSpecifiedDate(period.specifiedDate);
          } else if (period.scheduleType === 'minute') {
            setStartDate(period.startDate);
            setEndDate(period.endDate);
            setMinute(period.minute);
            setIsEndless(period.isEndless)
          } else if (period.scheduleType === 'daily') {
            setStartDate(period.startDate);
            setEndDate(period.endDate);
            setIsEndless(period.isEndless)
          } else if (period.scheduleType === 'weekly') {
            setEndDate(period.endDate);
            setSpecifiedTime(period.specifiedTime);
            setSpecifiedDay(options.dropdownDay.data.filter(x => period.specifiedDay.find(y => x.value === y.value)))
            setIsEndless(period.isEndless)
          } else if (period.scheduleType === 'monthly') {
            setEndDate(period.endDate);
            setSpecifiedTime(period.specifiedTime);
            setSpecifiedDay(options.dropdownDays.data.filter(x => period.specifiedDay.find(y => x.value === y.value)))
            setSpecifiedMonth(options.dropdownMonth.data.filter(x => period.specifiedMonth.find(y => x.value === y.value)))
            setIsEndless(period.isEndless)
          }
          setProfile({ trigger_profile_id: resp.data.id, trigger_rule_id: resp.data.trigger_rule.id })
        }
      }
      fetchData();
    }
  }, [automId])



  const handleClose = () => {
    setOpen(false);
  };
  const handleDisableEndDate = () => {
    setIsEndless((pre) => !pre)
    setEndDate('');
  };

  const AddZero = (item) => {
    if (item < 10) {
      return "0" + item;
    }
    return item;
  }

  const handleSelect = (event, setState) => {
    if (event.target.value === 'monthly' || event.target.value === 'weekly') {
      setSpecifiedDay([]);
      setSpecifiedMonth([]);
    }
    setEndDate('');
    setCurrentStartDate();
    setState(event.target.value);
    setIsEndless(false)
  };

  const setCurrentStartDate = () => {
    const currentDate = new Date();
    const dateTime = `${currentDate.getFullYear()}-${AddZero(currentDate.getMonth() + 1)}-${AddZero(currentDate.getDate())}T${AddZero(currentDate.getHours())
      }:${AddZero(currentDate.getMinutes())}`;
    setStartDate(dateTime);
  }

  async function handleSubmit() {
    let newForm;
    let isOk;
    if (scheduleType === 'once') {
      newForm = { scheduleType, specifiedDate };
      isOk = ruleOfDateTimers(scheduleType, { specifiedDate: specifiedDate })
    } else if (scheduleType === 'minute') {
      newForm = { scheduleType, startDate, endDate, minute, isEndless };
      isOk = ruleOfDateTimers(scheduleType, { startDate, endDate, minute })
    } else if (scheduleType === 'daily') {
      newForm = { scheduleType, startDate, endDate, isEndless };
      isOk = ruleOfDateTimers(scheduleType, { startDate, endDate })
    } else if (scheduleType === 'weekly') {
      newForm = { scheduleType, specifiedDay, endDate, specifiedTime, isEndless };
      isOk = ruleOfDateTimers(scheduleType, { startDate, endDate, specifiedDay, specifiedTime })
    } else if (scheduleType === 'monthly') {
      newForm = { scheduleType, specifiedMonth, endDate, specifiedTime, specifiedDay, isEndless };
      isOk = ruleOfDateTimers(scheduleType, { startDate, endDate, specifiedDay, specifiedTime, specifiedMonth })
    }
    if (!isOk) return
    setOpen(false);
    const newData = {
      period: JSON.stringify(newForm),
      autom_id: automId,
      type: profileType,
      trigger_profile_id: profile.trigger_profile_id,
      trigger_rule_id: profile.trigger_rule_id
    };
    const response = await triggerService.createProfile(newData);
    if (response) {
      if (response?.status) {
        setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.TRIGGER." + response.message), severity: 'success' }));
        setProfile({ trigger_profile_id: response.data.trigger_profile_id, trigger_rule_id: response.data.trigger_rule_id })
      } else {
        setAlert(alert => ({ ...alert, show: true, message: response.message, severity: 'error' }));
      }
    }
  }

  const ruleOfDateTimers = (scheduleType, dateTimes) => {
    const { specifiedDate, specifiedDay, specifiedMonth, startDate, endDate, minute } = dateTimes
    const current = Datetime.now().setZone(automDetail["time_zone"]);
    const currentMillis = current.toMillis();
    switch (scheduleType) {
      case 'once':
        let sDateO = DateTime.fromISO(specifiedDate, {zone: automDetail["time_zone"]});
        // const sDateO = new Date(specifiedDate);
        if (!specifiedDate || specifiedDate === '') {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.FILL-IN-THE-REQUIRED-FIELDS"), severity: 'error' }));
          return false
        }
        else if (sDateO.toMillis() <= currentMillis) {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.DATE-CANNOT-BE-LESS-THEN-CURRENT-TIME"), severity: 'error' }));
          return false
        }
        return true
      case 'minute':
        // let sDateM = new Date(startDate);
        let sDateM = DateTime.fromISO(startDate, {zone: automDetail["time_zone"]});
        let eDateM = DateTime.fromISO(endDate, {zone: automDetail["time_zone"]});
        // sDateM = DateTime.fromJSDate(sDateM, {zone: automDetail["time_zone"]});
        // const eDateM = new Date(endDate);
        if (minute <= 0) {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.MINUTE-CANNOT-BE-ZERO-OR-LESS"), severity: 'error' }));
          return false
        }
        else if (!startDate || startDate === '' || (!endDate && !isEndless)) {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.FILL-IN-THE-REQUIRED-FIELDS"), severity: 'error' }));
          return false
        }
        else if (sDateM.toMillis() <= currentMillis || eDateM.toMillis() <= currentMillis) {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.DATE-CANNOT-BE-LESS-THEN-CURRENT-TIME"), severity: 'error' }));
          return false
        }
        else if (eDateM.toMillis() < sDateM.toMillis()) {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.END-DATE-CANNOT-BE-LESS-THEN-START-DATE"), severity: 'error' }));
          return false
        }
        return true
      case 'daily':
        // const sDateD = new Date(startDate);
        let sDateD = DateTime.fromISO(startDate, {zone: automDetail["time_zone"]});
        let eDateD = DateTime.fromISO(endDate, {zone: automDetail["time_zone"]});
        // const eDateD = new Date(endDate);
        if (!startDate || startDate === '' || (!endDate && !isEndless)) {
          return setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.FILL-IN-THE-REQUIRED-FIELDS"), severity: 'error' }));
        }
        else if (sDateD.toMillis() <= currentMillis || eDateD.toMillis() <= currentMillis) {
          return setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.DATE-CANNOT-BE-LESS-THEN-CURRENT-TIME"), severity: 'error' }));
        }
        else if (eDateD.toMillis() < sDateD.toMillis()) {
          return setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.END-DATE-CANNOT-BE-LESS-THEN-START-DATE"), severity: 'error' }));
        }
        return true
      case 'weekly':
        let eDateW = DateTime.fromISO(endDate, {zone: automDetail["time_zone"]});
        // const eDateW = new Date(endDate);
        if (!specifiedDay || specifiedDay.length <= 0 || !specifiedTime || specifiedTime === '' || (!endDate && !isEndless)) {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.FILL-IN-THE-REQUIRED-FIELDS"), severity: 'error' }));
          return false
        }
        else if (eDateW.toMillis() <= currentMillis) {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.DATE-CANNOT-BE-LESS-THEN-CURRENT-TIME"), severity: 'error' }));
          return false
        }
        return true
      case 'monthly':
        let eDateMth = DateTime.fromISO(endDate, {zone: automDetail["time_zone"]});
        // const eDateMth = new Date(endDate);
        if (!specifiedMonth || specifiedMonth.length <= 0 || !specifiedDay || specifiedDay.length <= 0 || !specifiedTime || specifiedTime === "" || (!endDate && !isEndless)) {
          return setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.FILL-IN-THE-REQUIRED-FIELDS"), severity: 'error' }));
        }
        else if (eDateMth.toMillis() <= currentMillis) {
          return setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.AUTOMS.DATE-CANNOT-BE-LESS-THEN-CURRENT-TIME"), severity: 'error' }));
        }
        return true
      default:
        break;
    }
  }
  return (
    <Dialog open onClose={handleClose} PaperProps={{ style: { overflowX: 'hidden', minWidth: 400 } }}>
      <DialogTitle id="alert-dialog-title">
        <div id="about-title">
          <b>{t('CONTENT.LIBRARY.TIME-MANAGEMENT.ACTION.SCHEDULE')}</b>
          <Button onClick={handleClose}><Close /></Button>
        </div>
      </DialogTitle>
      <DialogContent>
        <div style={{ textAlign: 'left', marginTop: 5 }}>
          <div className="form-element">
            <div className="agent-time">
              <span>{`${t(label + "SELECTED-MATE-AGENT-IS")} ${automDetail["device_name"]}`}</span>
              <span>{t(label + "TIME-ZONE-IS")} {automDetail["time_zone"]}</span>
              <span>{t(label + "AGENT-TIME-IS")} <Clock timeZone={automDetail["time_zone"]} /></span>
            </div>
            <div style={{ marginBottom: 5 }}>{t(label + 'TIMING-TYPE')}</div>
            <Select classes={{ root: classes.select }} value={scheduleType} onChange={(e) => handleSelect(e, setScheduleType)} variant="outlined" fullWidth>
              <MenuItem classes={{ root: classes.select }} value="once">{t(label + 'ONLY-ONCE')}</MenuItem>
              <MenuItem classes={{ root: classes.select }} value="minute">{t(label + 'MINUTE')}</MenuItem>
              <MenuItem classes={{ root: classes.select }} value="daily">{t(label + 'DAILY')}</MenuItem>
              <MenuItem classes={{ root: classes.select }} value="weekly">{t(label + 'WEEKLY')}</MenuItem>
              <MenuItem classes={{ root: classes.select }} value="monthly">{t(label + 'MONTHLY')}</MenuItem>
            </Select>
          </div>
        </div>
        {scheduleType === 'once' && (
          <div className="form-element">
            {t(label + 'DATE-TIME')}
            <TextField type="datetime-local" fullWidth value={specifiedDate}
              onChange={e => setSpecifiedDate(e.target.value)}
              InputLabelProps={{ shrink: true }} size="small"
            />
          </div>
        )}
        {scheduleType === 'minute' && (
          <div>
            <div className="form-element">
              {t(label + 'START-DATE-TIME')} <span className='mandatory-field'>*</span>
              <TextField type="datetime-local" fullWidth value={startDate}
                onChange={e => { e.target.value !== '' ? setStartDate(e.target.value) : setCurrentStartDate() }}
                InputLabelProps={{ shrink: true }} size="small"
              />
            </div>
            <div className="form-element">
              {t(label + 'MINUTE')} <span className='mandatory-field'>*</span>
              <TextField type="number" fullWidth value={minute}
                onChange={e => setMinute(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            {/*  */}
            <div className="form-element">
              <FormControlLabel
                className='disable-end-date-checkbox'
                control={
                  <Checkbox
                    checked={isEndless}
                    onChange={handleDisableEndDate}
                    name="disableEndDate"
                    color="#C4C4C4"
                    size="small"
                  />
                }
                label={t(label + 'DISABLE-END-DATE')} />
            </div>
            <div className="form-element">
              {t(label + 'END-DATE')} {!isEndless && <span className='mandatory-field'>*</span>}
              <TextField type="datetime-local" fullWidth value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isEndless}
              />
            </div>
          </div>
        )}
        {scheduleType === 'daily' && (
          <div>
            <div className="form-element">
              {t(label + 'START-DATE-TIME')} <span className='mandatory-field'>*</span>
              <TextField type="datetime-local" fullWidth value={startDate}
                onChange={e => { e.target.value !== '' ? setStartDate(e.target.value) : setCurrentStartDate() }}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div className="form-element">
              <FormControlLabel
                className='disable-end-date-checkbox'
                control={
                  <Checkbox
                    checked={isEndless}
                    onChange={handleDisableEndDate}
                    name="disableEndDate"
                    color="#C4C4C4"
                    size="small"
                  />
                }
                label={t(label + 'DISABLE-END-DATE')} />
            </div>
            <div className="form-element">
              {t(label + 'END-DATE')} {!isEndless && <span className='mandatory-field'>*</span>}
              <TextField type="date" fullWidth value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isEndless}
              />
            </div>
          </div>
        )}
        {scheduleType === 'weekly' && (
          <div>
            <div className="form-element">
              {t(label + 'DAY')} <span className='mandatory-field'>*</span>
              <Autocomplete multiple value={specifiedDay}
                options={options.dropdownDay.data}
                getOptionLabel={option => { return `${t(option.label)}`; }}
                renderInput={params => <TextField {...params} variant="outlined" />}
                onChange={(e, value) => setSpecifiedDay(value)}
                filterSelectedOptions size="small"
              />
            </div>
            <div className="form-element">
              {t(label + 'TIME')} <span className='mandatory-field'>*</span>
              <TextField type="time" value={specifiedTime} fullWidth
                onChange={e => setSpecifiedTime(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small"
              />
            </div>
            <div className="form-element">
              <FormControlLabel
                className='disable-end-date-checkbox'
                control={
                  <Checkbox
                    checked={isEndless}
                    onChange={handleDisableEndDate}
                    name="disableEndDate"
                    color="#C4C4C4"
                    size="small"
                  />
                }
                label={t(label + 'DISABLE-END-DATE')} />
            </div>
            <div className="form-element">
              {t(label + 'END-DATE')} {!isEndless && <span className='mandatory-field'>*</span>}
              <TextField type="date" fullWidth value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small"
                disabled={isEndless}
              />
            </div>
          </div>
        )}
        {scheduleType === 'monthly' && (
          <div>
            <div className="form-element">
              {t(label + 'MONTH')} <span className='mandatory-field'>*</span>
              <Autocomplete multiple value={specifiedMonth}
                options={options.dropdownMonth.data}
                getOptionLabel={option => { return `${t(option.label)}`; }}
                onChange={(e, value) => setSpecifiedMonth(value)}
                renderInput={params => <TextField {...params} variant="outlined" />}
                filterSelectedOptions size="small"
              />
            </div>
            <div className="form-element">
              {t(label + 'DAY')} <span className='mandatory-field'>*</span>
              <Autocomplete multiple value={specifiedDay}
                options={dayOptions}
                getOptionLabel={option => { return `${t(option.label)}`; }}
                onChange={(e, value) => setSpecifiedDay(value)}
                renderInput={params => <TextField {...params} variant="outlined" />}
                disabled={specifiedMonth.length === 0}
                filterSelectedOptions size="small"
              />
            </div>
            <div className="form-element">
              {t(label + 'TIME')} <span className='mandatory-field'>*</span>
              <TextField type="time" fullWidth value={specifiedTime}
                onChange={e => setSpecifiedTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div className="form-element">
              <FormControlLabel
                className='disable-end-date-checkbox'
                control={
                  <Checkbox
                    checked={isEndless}
                    onChange={handleDisableEndDate}
                    name="disableEndDate"
                    color="#C4C4C4"
                    size="small"
                  />
                }
                label={t(label + 'DISABLE-END-DATE')} />
            </div>
            <div className="form-element">
              {t(label + 'END-DATE')} {!isEndless && <span className='mandatory-field'>*</span>}
              <TextField type="date" fullWidth value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small"
                disabled={isEndless}
              />
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between', padding: '8px 24px 16px 24px' }}>
        <div className={classes.bottomSection}>
          <button type="button" onClick={handleSubmit} className={`${classes.button} ${classes.submit}`}
            style={{ backgroundColor: '#46474E', borderRadius: 2, border: 'none', color: '#fff' }}
          >
            {t('CONTENT.AUTOMS-LIST.EDIT')}
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default Schedule;
